/**
 * PE LP Interest Valuation Page
 *
 * Form-driven LP interest valuation with discount breakdown,
 * unfunded liability analysis, and secondary market estimates.
 * Calls the PE Valuation API at /api/v1/pe/valuation/lp-interest.
 */
import { useState } from 'react';
import {
  Card,
  Heading,
  BodyText,
  FormLabel,
  Input,
  Button,
  Select,
  MetricDisplay,
  FormHelperText,
  type SelectOption,
  TableScroll,
} from '@aaimplatform/ui';
import { Switch } from '@aaimplatform/ui';
import { createPeValuationClient } from '@aaimplatform/institutional-types/api-clients';
import { csrfMiddleware } from '../api/client';

const peValuationClient = createPeValuationClient();
// CSRF double-submit echo (N8) on mutating PE-valuation requests.
peValuationClient.use(csrfMiddleware);

const FUND_STRATEGY_OPTIONS: SelectOption[] = [
  { value: 'large_cap_buyout', label: 'Large Cap Buyout' },
  { value: 'mid_market_buyout', label: 'Mid Market Buyout' },
  { value: 'small_cap_buyout', label: 'Small Cap Buyout' },
  { value: 'growth_equity', label: 'Growth Equity' },
  { value: 'venture_capital', label: 'Venture Capital' },
  { value: 'permanent_capital', label: 'Permanent Capital' },
  { value: 'co_investment', label: 'Co-Investment' },
  { value: 'secondary', label: 'Secondary' },
];

const GP_TIER_OPTIONS: SelectOption[] = [
  { value: 'top_quartile', label: 'Top Quartile' },
  { value: 'second_quartile', label: 'Second Quartile' },
  { value: 'third_quartile', label: 'Third Quartile' },
  { value: 'bottom_quartile', label: 'Bottom Quartile' },
];

import type { components } from '@aaimplatform/institutional-types/pe-valuation';

type LpInterestValuationResponse = components['schemas']['LpInterestValuationResponse'];

// Internal: not an API contract
interface FormState {
  fundId: string;
  commitmentAmount: string;
  calledCapitalPercent: string;
  currentFundNav: string;
  fundStrategy: string;
  gpTier: string;
  includeUnfundedLiability: boolean;
}

const INITIAL_FORM: FormState = {
  fundId: '',
  commitmentAmount: '',
  calledCapitalPercent: '',
  currentFundNav: '',
  fundStrategy: '',
  gpTier: '',
  includeUnfundedLiability: true,
};

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatPercent(decimal: number): string {
  return `${(decimal * 100).toFixed(2)}%`;
}

export function PeLpValuationPage() {
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [result, setResult] = useState<LpInterestValuationResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateField = (field: keyof FormState, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const isFormValid =
    form.fundId.trim() !== '' &&
    form.commitmentAmount !== '' &&
    form.calledCapitalPercent !== '' &&
    form.currentFundNav !== '' &&
    form.fundStrategy !== '' &&
    form.gpTier !== '';

  const handleSubmit = async () => {
    if (!isFormValid) return;
    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      type FundStrategy = NonNullable<components['schemas']['LpInterestValuationRequest']['fundStrategy']>;
      type GpTier = NonNullable<components['schemas']['LpInterestValuationRequest']['gpTier']>;

      const { data, error: apiError } = await peValuationClient.POST(
        '/api/v1/pe/valuation/lp-interest',
        {
          body: {
            fundId: form.fundId.trim(),
            commitmentAmount: parseFloat(form.commitmentAmount),
            calledCapitalPercent: parseFloat(form.calledCapitalPercent),
            currentFundNav: parseFloat(form.currentFundNav),
            fundStrategy: form.fundStrategy as FundStrategy,
            gpTier: form.gpTier as GpTier,
            includeUnfundedLiability: form.includeUnfundedLiability,
          },
        }
      );
      if (apiError || !data) {
        const msg = (apiError as { message?: string })?.message
          ?? 'Failed to retrieve valuation. Verify the PE Valuation service is available.';
        throw new Error(msg);
      }
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to retrieve valuation.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <BodyText className="text-secondary mt-2">
            Estimate fair value of limited partner interests with discount analysis
            and secondary market positioning
          </BodyText>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left: Valuation Form */}
        <Card>
          <Heading level={3} as={3} size="card" className="mb-4">Valuation Parameters</Heading>
          <div className="space-y-4">
            <div>
              <FormLabel htmlFor="fundId">Fund ID</FormLabel>
              <Input
                id="fundId"
                type="text"
                value={form.fundId}
                onChange={(e) => updateField('fundId', e.target.value)}
                placeholder="Enter fund identifier"
              />
            </div>

            <div>
              <FormLabel htmlFor="commitmentAmount">Commitment Amount</FormLabel>
              <Input
                id="commitmentAmount"
                type="number"
                min={0}
                step={100000}
                value={form.commitmentAmount}
                onChange={(e) => updateField('commitmentAmount', e.target.value)}
                placeholder="e.g. 10000000"
              />
              <FormHelperText>
                {form.commitmentAmount
                  ? formatCurrency(parseFloat(form.commitmentAmount))
                  : 'Total LP commitment in USD'}
              </FormHelperText>
            </div>

            <div>
              <FormLabel htmlFor="calledCapitalPercent">Called Capital (%)</FormLabel>
              <Input
                id="calledCapitalPercent"
                type="number"
                min={0}
                max={100}
                step={0.1}
                value={form.calledCapitalPercent}
                onChange={(e) => updateField('calledCapitalPercent', e.target.value)}
                placeholder="e.g. 65"
              />
              <FormHelperText>Percentage of commitment called (0-100)</FormHelperText>
            </div>

            <div>
              <FormLabel htmlFor="currentFundNav">Current Fund NAV</FormLabel>
              <Input
                id="currentFundNav"
                type="number"
                min={0}
                step={100000}
                value={form.currentFundNav}
                onChange={(e) => updateField('currentFundNav', e.target.value)}
                placeholder="e.g. 850000000"
              />
              <FormHelperText>
                {form.currentFundNav
                  ? formatCurrency(parseFloat(form.currentFundNav))
                  : 'Net asset value of the fund'}
              </FormHelperText>
            </div>

            <div>
              <FormLabel>Fund Strategy</FormLabel>
              <Select
                options={FUND_STRATEGY_OPTIONS}
                value={form.fundStrategy}
                onChange={(e) => updateField('fundStrategy', e.target.value)}
                placeholder="Select strategy"
              />
            </div>

            <div>
              <FormLabel>GP Tier</FormLabel>
              <Select
                options={GP_TIER_OPTIONS}
                value={form.gpTier}
                onChange={(e) => updateField('gpTier', e.target.value)}
                placeholder="Select GP quartile"
              />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Switch
                isSelected={form.includeUnfundedLiability}
                onChange={(val: boolean) => updateField('includeUnfundedLiability', val)}
                label="Include Unfunded Liability"
                description="Account for remaining unfunded commitment in net value"
              />
            </div>

            <div className="pt-4">
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={!isFormValid || isLoading}
                className="w-full"
              >
                {isLoading ? 'Calculating...' : 'Calculate Valuation'}
              </Button>
            </div>
          </div>
        </Card>

        {/* Right: Results Panel */}
        <div className="space-y-4">
          {isLoading && (
            <Card>
              <BodyText className="text-secondary text-center py-8">
                Computing LP interest valuation...
              </BodyText>
            </Card>
          )}

          {error && (
            <Card>
              <div className="p-4 bg-error/10 border border-error/20 rounded">
                <BodyText className="text-error font-semibold">Valuation Error</BodyText>
                <BodyText className="text-error mt-1">{error}</BodyText>
              </div>
            </Card>
          )}

          {result && !isLoading && (
            <>
              {/* Primary Values */}
              <Card>
                <Heading level={3} as={3} size="card" className="mb-4">Valuation Summary</Heading>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-3 border-b border-surface">
                    <BodyText className="text-secondary">Gross NAV Value</BodyText>
                    <MetricDisplay
                      value={formatCurrency(result.grossNavValue)}
                      className="text-primary"
                    />
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-surface">
                    <div>
                      <BodyText className="text-secondary">Adjusted Value</BodyText>
                      <BodyText className="text-tertiary text-xs mt-0.5">
                        Range: {formatCurrency(result.adjustedValueRangeLow ?? 0)} &ndash;{' '}
                        {formatCurrency(result.adjustedValueRangeHigh ?? 0)}
                      </BodyText>
                    </div>
                    <MetricDisplay
                      value={formatCurrency(result.adjustedValue)}
                      className="text-primary"
                    />
                  </div>
                  {form.includeUnfundedLiability && (
                    <div className="flex justify-between items-center py-3 border-b border-surface">
                      <BodyText className="text-secondary">Unfunded Liability</BodyText>
                      <MetricDisplay
                        value={`(${formatCurrency(result.unfundedLiability ?? 0)})`}
                        className="text-error"
                      />
                    </div>
                  )}
                  <div className="flex justify-between items-center py-3 bg-accent/5 px-4 -mx-4 rounded">
                    <BodyText className="font-semibold text-accent">Net Value</BodyText>
                    <MetricDisplay
                      value={formatCurrency(result.netValue ?? 0)}
                      className="text-2xl text-accent font-semibold"
                    />
                  </div>
                  <div className="flex justify-between items-center py-3 border-t border-surface">
                    <BodyText className="text-secondary">Secondary Market Estimate</BodyText>
                    <MetricDisplay
                      value={formatCurrency(result.secondaryMarketEstimate ?? 0)}
                      className="text-primary"
                    />
                  </div>
                </div>
              </Card>

              {/* Discount Breakdown */}
              <Card>
                <Heading level={3} as={3} size="card" className="mb-4">Discount Breakdown</Heading>
                <TableScroll label="Discount breakdown">
                  <table className="w-full text-sm font-sans">
                    <thead>
                      <tr className="border-b border-recessed">
                        <th className="text-left py-2 text-secondary font-medium">Factor</th>
                        <th className="text-right py-2 text-secondary font-medium">Discount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-surface">
                        <td className="py-2 text-primary">Illiquidity</td>
                        <td className="py-2 text-right font-mono text-primary">
                          {formatPercent(result.discounts.illiquidityDiscount ?? 0)}
                        </td>
                      </tr>
                      <tr className="border-b border-surface">
                        <td className="py-2 text-primary">Uncertainty</td>
                        <td className="py-2 text-right font-mono text-primary">
                          {formatPercent(result.discounts.valuationUncertaintyDiscount ?? 0)}
                        </td>
                      </tr>
                      <tr className="border-b border-surface">
                        <td className="py-2 text-primary">Transfer Restriction</td>
                        <td className="py-2 text-right font-mono text-primary">
                          {formatPercent(result.discounts.transferRestrictionDiscount ?? 0)}
                        </td>
                      </tr>
                      <tr className="border-b border-surface">
                        <td className="py-2 text-primary">J-Curve</td>
                        <td className="py-2 text-right font-mono text-primary">
                          {formatPercent(result.discounts.jCurveDiscount ?? 0)}
                        </td>
                      </tr>
                      <tr className="border-b border-surface">
                        <td className="py-2 text-primary">Concentration</td>
                        <td className="py-2 text-right font-mono text-primary">
                          {formatPercent(result.discounts.concentrationDiscount ?? 0)}
                        </td>
                      </tr>
                      <tr className="bg-recessed">
                        <td className="py-2 font-semibold text-primary">Total Discount</td>
                        <td className="py-2 text-right font-mono font-semibold text-accent">
                          {formatPercent(result.discounts.totalDiscountPercent ?? 0)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </TableScroll>
              </Card>
            </>
          )}

          {!result && !isLoading && !error && (
            <Card>
              <BodyText className="text-secondary text-center py-8">
                Enter valuation parameters and submit to view results
              </BodyText>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
